/*
 * Custom Sweets — native cart drawer.
 *
 * Uses Shopify's real cart (/cart/add.js, /cart/change.js, /cart.js) so multiple
 * products accumulate normally instead of each "Add to basket" click spawning its
 * own isolated checkout. Tiered product-page prices aren't something the native cart
 * understands (it only knows variant.price × quantity), so tiered lines carry their
 * already-quoted price as line item properties (_cm_tiered, _cm_handle, Size,
 * _cm_unit, _cm_total) purely for display here. The actual checkout charge is
 * corrected separately — see checkout() below — by asking the proxy to re-validate
 * every tiered line server-side and apply a one-time discount code for the exact
 * difference before handing off to Shopify's real checkout.
 *
 * NOTE: this pointed at Custom Magnets' own proxy server. Tiered-priced checkout
 * will not work until you deploy your own proxy (same shape as cm-quote-proxy)
 * for Custom Sweets and set PROXY below.
 */
(function () {
  var PROXY = '';

  function money(cents) { return '£' + (cents / 100).toFixed(2); }

  function els() {
    return {
      root: document.getElementById('cm-cart-drawer'),
      overlay: document.getElementById('cm-cart-overlay'),
      body: document.getElementById('cm-cart-body'),
      subtotal: document.getElementById('cm-cart-subtotal'),
      checkoutBtn: document.getElementById('cm-cart-checkout'),
      error: document.getElementById('cm-cart-error'),
      empty: document.getElementById('cm-cart-empty')
    };
  }

  function open() {
    var e = els();
    if (!e.root) return;
    e.root.classList.add('open');
    e.root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refresh();
  }

  function close() {
    var e = els();
    if (!e.root) return;
    e.root.classList.remove('open');
    e.root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function lineRow(item) {
    var props = item.properties || {};
    var tiered = props._cm_tiered === '1';
    var size = props.Size || (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
    var priceHtml, qtyHtml;

    if (tiered) {
      priceHtml = '<div class="cm-cart-item__price">' + (props._cm_total || money(item.final_line_price)) + '</div>' +
        (props._cm_unit ? '<div class="cm-cart-item__unit">' + props._cm_unit + ' / unit</div>' : '');
      qtyHtml = '<div class="cm-cart-item__qty-static">Qty: ' + item.quantity.toLocaleString() + '</div>' +
        '<div class="cm-cart-item__hint">To change quantity, edit on the product page</div>';
    } else {
      priceHtml = '<div class="cm-cart-item__price">' + money(item.final_line_price) + '</div>';
      qtyHtml = '<div class="cm-cart-item__stepper">' +
        '<button type="button" class="cm-cart-qty-btn" data-action="dec" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Decrease quantity">−</button>' +
        '<span>' + item.quantity + '</span>' +
        '<button type="button" class="cm-cart-qty-btn" data-action="inc" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Increase quantity">+</button>' +
        '</div>';
    }

    return '<div class="cm-cart-item" data-key="' + item.key + '">' +
      '<div class="cm-cart-item__img"><img src="' + (item.image || '') + '" alt="" loading="lazy"></div>' +
      '<div class="cm-cart-item__body">' +
        '<div class="cm-cart-item__title">' + item.product_title + '</div>' +
        (size ? '<div class="cm-cart-item__size">' + size + '</div>' : '') +
        qtyHtml +
        '<button type="button" class="cm-cart-item__remove" data-key="' + item.key + '">Remove</button>' +
      '</div>' +
      priceHtml +
    '</div>';
  }

  function computeSubtotal(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props._cm_tiered === '1' && props._cm_total) {
        total += parseFloat(String(props._cm_total).replace(/[^0-9.]/g, '')) || 0;
      } else {
        total += item.final_line_price / 100;
      }
    });
    return total;
  }

  /*
   * Groups tiered cart lines by handle+size, summing quantity across native lines.
   * A customer who clicks "Add to basket" for the same product/size more than once
   * (e.g. 100 units, then another 100) ends up with two separate cart lines rather
   * than one merged 200-unit line — Shopify only auto-merges lines whose properties
   * are byte-identical, which tiered lines can't rely on. Both the checkout price
   * correction and the display subtotal correction need the true combined quantity
   * per handle+size, not one entry per native line.
   */
  function groupTieredLines(cart) {
    var grouped = {};
    cart.items.forEach(function (item) {
      if (!item.properties || item.properties._cm_tiered !== '1') return;
      var handle = item.properties._cm_handle, size = item.properties.Size;
      var key = handle + ' ' + size;
      if (!grouped[key]) grouped[key] = { handle: handle, size: size, quantity: 0 };
      grouped[key].quantity += item.quantity;
    });
    return grouped;
  }

  /*
   * The initial render shows each tiered line's own stashed _cm_total summed —
   * accurate for a single line, but wrong when the same product/size was added
   * more than once (two 100-unit lines don't sum to what 200 combined units
   * actually cost on the pricing_table). This asks the proxy's read-only
   * /tiered-quote for the true combined-quantity price per handle+size and patches
   * the displayed subtotal once it resolves, without blocking the initial render.
   * Fails silently, leaving the stale (approximate) subtotal in place — this is
   * display-only, the real checkout charge is always corrected by checkout() below
   * regardless of whether this succeeds. With PROXY unset (the default here until
   * a proxy is deployed for Custom Sweets), this fetch simply fails and the catch
   * below absorbs it — no error surfaces to the customer.
   */
  function correctSubtotal(cart) {
    var grouped = groupTieredLines(cart);
    var groupKeys = Object.keys(grouped);
    if (!groupKeys.length) return;

    var nonTieredTotal = 0;
    cart.items.forEach(function (item) {
      if (!item.properties || item.properties._cm_tiered !== '1') nonTieredTotal += item.final_line_price / 100;
    });

    var lines = groupKeys.map(function (key) { return grouped[key]; });

    fetch(PROXY + '/tiered-quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines: lines })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (res) {
        if (!res || !res.lines) return;
        var quoted = {};
        res.lines.forEach(function (l) { quoted[l.handle + ' ' + l.size] = l.total; });

        // Only patch the subtotal if every group resolved — partial data would
        // understate any group that failed to quote.
        if (!groupKeys.every(function (key) { return typeof quoted[key] === 'number'; })) return;

        var tieredTotal = 0;
        groupKeys.forEach(function (key) { tieredTotal += quoted[key]; });

        var e = els();
        if (e.subtotal) e.subtotal.textContent = '£' + (nonTieredTotal + tieredTotal).toFixed(2);
      })
      .catch(function () {});
  }

  function render(cart) {
    var e = els();
    if (!e.body) return;

    if (!cart.items.length) {
      e.body.innerHTML = '';
      if (e.empty) e.empty.style.display = 'flex';
      if (e.checkoutBtn) e.checkoutBtn.disabled = true;
      if (e.subtotal) e.subtotal.textContent = '£0.00';
      return;
    }

    if (e.empty) e.empty.style.display = 'none';
    if (e.checkoutBtn) e.checkoutBtn.disabled = false;
    e.body.innerHTML = cart.items.map(lineRow).join('');
    if (e.subtotal) e.subtotal.textContent = '£' + computeSubtotal(cart).toFixed(2);

    e.body.querySelectorAll('.cm-cart-item__remove').forEach(function (btn) {
      btn.addEventListener('click', function () { changeLine(btn.dataset.key, 0); });
    });
    e.body.querySelectorAll('.cm-cart-qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var qty = parseInt(btn.dataset.qty, 10);
        var next = btn.dataset.action === 'inc' ? qty + 1 : Math.max(0, qty - 1);
        changeLine(btn.dataset.key, next);
      });
    });
  }

  function refresh() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        correctSubtotal(cart);
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return cart;
      })
      .catch(function () {});
  }

  function changeLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { refresh(); })
      .catch(function () {});
  }

  /**
   * Adds an item to Shopify's real cart, then opens the drawer.
   * payload: { id, quantity, properties }
   */
  function addToCart(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'Could not add to basket'); });
        return r.json();
      })
      .then(function (item) {
        return refresh().then(function () { open(); return item; });
      });
  }

  function showError(msg) {
    var e = els();
    if (!e.error) return;
    e.error.textContent = msg;
    e.error.style.display = 'block';
  }

  function hideError() {
    var e = els();
    if (e.error) e.error.style.display = 'none';
  }

  /**
   * Re-validates every tiered line in the cart against the server, gets a one-time
   * discount code for the true tiered total, and hands off to Shopify's real
   * checkout with it applied. If the cart has no tiered lines, native pricing is
   * already correct and checkout proceeds directly — no proxy round-trip needed.
   */
  function checkout() {
    var e = els();
    hideError();
    if (e.checkoutBtn) {
      e.checkoutBtn.disabled = true;
      e.checkoutBtn.textContent = 'Confirming your price…';
    }

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var grouped = groupTieredLines(cart);
        var tieredLines = Object.keys(grouped).map(function (key) { return grouped[key]; });

        if (!tieredLines.length) {
          window.location.href = '/checkout';
          return;
        }

        if (!PROXY) throw new Error('Tiered pricing isn\'t connected yet — set PROXY in cm-cart-drawer.js to your proxy server URL.');

        return fetch(PROXY + '/cart-discount', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lines: tieredLines })
        })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.body && res.body.error ? res.body.error : 'Could not confirm your price');
            var url = '/checkout';
            if (res.body.discount_code) url += '?discount=' + encodeURIComponent(res.body.discount_code);
            window.location.href = url;
          });
      })
      .catch(function (err) {
        if (e.checkoutBtn) {
          e.checkoutBtn.disabled = false;
          e.checkoutBtn.textContent = 'Checkout →';
        }
        showError(err.message || 'Something went wrong confirming your price — please try again or contact us.');
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var e = els();
    if (e.overlay) e.overlay.addEventListener('click', close);
    var closeBtn = document.getElementById('cm-cart-close');
    if (closeBtn) closeBtn.addEventListener('click', close);
    var continueBtn = document.getElementById('cm-cart-continue');
    if (continueBtn) continueBtn.addEventListener('click', close);
    if (e.checkoutBtn) e.checkoutBtn.addEventListener('click', checkout);
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && e.root && e.root.classList.contains('open')) close();
    });
    var trigger = document.getElementById('cm-cart-icon');
    if (trigger) {
      trigger.addEventListener('click', function (evt) {
        evt.preventDefault();
        open();
      });
    }

    /*
     * Horizon's native /cart page (main-cart.liquid → cart-summary.liquid) has its
     * own "Checkout" button (#checkout) that we don't render ourselves, so it can't
     * be wired up above. Intercepting it via delegation — rather than editing that
     * Horizon snippet directly — means a customer who lands on /cart directly still
     * goes through the same price-correction step as the drawer's own button,
     * without touching Horizon core files.
     */
    document.addEventListener('click', function (evt) {
      var nativeCheckoutBtn = evt.target.closest && evt.target.closest('#checkout');
      if (nativeCheckoutBtn) {
        evt.preventDefault();
        checkout();
      }
    });

    refresh();
  });

  window.CMCart = { open: open, close: close, refresh: refresh, addToCart: addToCart, checkout: checkout };
})();
